<script lang="ts" setup>
import { ref, computed, watch } from "vue"
import { useMotion } from "@vueuse/motion"
import { useSpring } from "@/composables/use-spring"
import { useMenuGeometry } from "@/composables/use-menu-geometry"
import type { MenuState } from "@/composables/use-menu-machine"

const { state, hidden = false } = defineProps<{
  state: MenuState
  // Hide the whole menu pill (e.g. while the student-page zoom is active).
  hidden?: boolean
}>()

const geometry = useMenuGeometry()

// Collapsed pill geometry, mobile-aware.
const closedHeight = computed(() => {
  return geometry.collapsedHeight("closed")
})
const hoverHeight = computed(() => {
  return geometry.collapsedHeight("hovering")
})
// Height of the clickable toggle overlay at the top of the closed pill.
const toggleHeight = geometry.toggleHeight

// Header positioning. While open it sits in normal flow above the content;
// while collapsed it's a top-anchored white overlay filling the pill.
const headerClass = computed(() => {
  if (state === "nav-open" || state === "content-open") return "relative"
  return "absolute inset-0"
})

const openLargeWidth = geometry.openLargeWidth
const openSmallWidth = geometry.openSmallWidth
const openHeight = geometry.openHeight

const rootRef = ref<HTMLElement>()
const { config } = useSpring("snappy")

const transition = {
  height: config,
  width: { ...config, delay: 250 },
}

const { apply } = useMotion(rootRef, {
  initial: { width: geometry.containerWidth.value, height: closedHeight.value },
})

const applyOpenVariant = (s: MenuState, withTransition = true) => {
  const width =
    s === "content-open" ? openLargeWidth.value : openSmallWidth.value
  apply({
    width,
    height: openHeight.value,
    ...(withTransition ? { transition } : {}),
  })
}

// Collapsed states are applied explicitly (rather than via named variants) so
// the height can track the closed/hover profile values.
const applyCollapsed = (s: MenuState) => {
  apply({
    width: geometry.containerWidth.value,
    height: s === "hovering" ? hoverHeight.value : closedHeight.value,
    transition,
  })
}

watch(
  () => state,
  val => {
    if (val === "content-open" || val === "nav-open") {
      applyOpenVariant(val)
    } else {
      applyCollapsed(val)
    }
  },
)

// Apply the initial collapsed geometry on mount.
if (state === "closed" || state === "hovering") {
  applyCollapsed(state)
}

watch([geometry.windowWidth, geometry.windowHeight], () => {
  if (state === "content-open" || state === "nav-open") {
    applyOpenVariant(state, false)
  } else {
    // Re-apply collapsed geometry so the pill picks up mobile/desktop sizing
    // when the viewport crosses MOBILE_BREAKPOINT.
    applyCollapsed(state)
  }
})

const emit = defineEmits<{
  "hover-enter": []
  "hover-leave": []
  close: []
  toggle: []
}>()

const onMouseEnter = () => emit("hover-enter")
const onMouseLeave = () => emit("hover-leave")
const onBlockerClick = () => emit("close")
const onToggleClick = () => emit("toggle")
</script>

<template>
  <div
    ref="rootRef"
    class="fixed top-3 left-3 border bg-white z-50 overflow-hidden max-h-[calc(100vh-24px)] transition-[filter,border-radius,opacity] duration-300"
    :class="{
      'opacity-0 pointer-events-none': hidden,
    }"
  >
    <div class="h-full flex flex-col relative">
      <!-- Header: white overlay; in flow when open, fills the pill when collapsed -->
      <div class="bg-white z-20" :class="headerClass">
        <div
          v-if="state === 'closed' || state === 'hovering'"
          class="absolute top-0 left-0 w-full cursor-pointer z-50"
          @mouseenter="onMouseEnter"
          @mouseleave="onMouseLeave"
          @click="onToggleClick"
          :style="{
            height: `${toggleHeight}px`,
          }"
        ></div>
        <slot name="header" />
      </div>
      <div class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden relative">
        <slot />
      </div>
    </div>
  </div>
  <Teleport to="body">
    <div
      @click="onBlockerClick"
      v-if="state === 'content-open' || state === 'nav-open'"
      class="fixed inset-0 bg-white/20 z-40"
    ></div>
  </Teleport>
</template>
